/**
 * The roster walk's session state (PRD-36 Phase 2, FR-2.1–2.4).
 *
 * The scan flow was serialized to one sheet: photograph, wait out the whole
 * OCR-match-grade pipeline, land on a result screen, go back, pick the next
 * student. The backend has always reserved FIVE concurrent slots per professor
 * (`OCR_MAX_IN_FLIGHT_PER_PROFESSOR`) and the UI used one of them, so a class
 * of thirty cost about thirty times the pipeline's latency — roughly half an
 * hour standing at a desk, most of it watching a progress bar.
 *
 * The walk decouples capture from the run. Photograph a student, the sheet
 * dispatches, the cursor advances to the next student immediately, and each
 * row reports its own outcome when its own run settles. Nothing waits for
 * anything except the five-slot cap, and the cap is handled by holding the
 * sixth capture until a slot frees rather than by refusing it.
 *
 * Pure: no React, no i18n. The page renders this; it decides nothing.
 */
import type { StudentProgressRow } from "@/types"

/** Mirrors `settings.OCR_MAX_IN_FLIGHT_PER_PROFESSOR` (api/progress.py). */
export const IN_FLIGHT_CAP = 5

/** Terminal states a row can settle into. */
export type WalkOutcome = "graded" | "needs_review" | "failed" | "skipped"

export type WalkState =
  /** Captured, waiting for one of the five slots. */
  | "queued"
  /** The POST is in flight. */
  | "dispatching"
  /** The worker owns it; its TaskRun drives the row. */
  | "processing"
  | WalkOutcome

export interface WalkEntry {
  state: WalkState
  uploadId?: string
  taskRunId?: string | null
  errorCode?: string | null
  /** Held only while queued — the bytes are released the moment they dispatch. */
  files?: File[]
}

/** studentId → entry. Absent means "not attempted in this session". */
export type WalkSession = Record<string, WalkEntry>

const SETTLED: ReadonlySet<WalkState> = new Set<WalkState>([
  "graded",
  "needs_review",
  "failed",
  "skipped",
])

export const isSettled = (state: WalkState): boolean => SETTLED.has(state)

/**
 * How many of the professor's five slots this session is holding.
 *
 * `dispatching` counts: the row is about to exist server-side, and counting it
 * only after the POST returns is how you send a sixth request that 429s.
 */
export function inFlightCount(session: WalkSession): number {
  return Object.values(session).filter(
    (entry) => entry.state === "dispatching" || entry.state === "processing",
  ).length
}

export function hasFreeSlot(session: WalkSession): boolean {
  return inFlightCount(session) < IN_FLIGHT_CAP
}

/** The first student this session has not touched, in roster order. */
export function nextTarget(
  students: ReadonlyArray<StudentProgressRow>,
  session: WalkSession,
  taskId: string,
): string | null {
  for (const student of students) {
    if (session[student.id]) continue
    // FR-2.3 — a student who already has a grade for this task is skipped by
    // default. Not hidden: the row stays, says so, and offers a re-scan.
    if (gradeFor(student, taskId) !== null) continue
    return student.id
  }
  return null
}

export const gradeFor = (student: StudentProgressRow, taskId: string): number | null =>
  (taskId ? student.records[taskId]?.grade : null) ?? null

export interface WalkCounts {
  /** Students with a grade for this task before the session started. */
  already: number
  graded: number
  needsReview: number
  failed: number
  inFlight: number
  /** Not attempted and not already graded. */
  remaining: number
}

export function walkCounts(
  students: ReadonlyArray<StudentProgressRow>,
  session: WalkSession,
  taskId: string,
): WalkCounts {
  const counts: WalkCounts = {
    already: 0,
    graded: 0,
    needsReview: 0,
    failed: 0,
    inFlight: 0,
    remaining: 0,
  }
  for (const student of students) {
    const entry = session[student.id]
    if (entry) {
      if (entry.state === "graded") counts.graded += 1
      else if (entry.state === "needs_review") counts.needsReview += 1
      else if (entry.state === "failed") counts.failed += 1
      else if (entry.state === "skipped") counts.already += 1
      else counts.inFlight += 1
      continue
    }
    if (gradeFor(student, taskId) !== null) counts.already += 1
    else counts.remaining += 1
  }
  return counts
}

/**
 * The upload ids this session routed to review, in roster order — what the
 * end-of-session hand-off needs (FR-2.4). A `needs_review` row never
 * interrupts the walk; it collects here and the professor confirms the batch
 * in the queue when the stack of paper is done.
 */
export function reviewUploadIds(
  students: ReadonlyArray<StudentProgressRow>,
  session: WalkSession,
): string[] {
  return students
    .map((student) => session[student.id])
    .filter((entry) => entry?.state === "needs_review" && entry.uploadId)
    .map((entry) => entry!.uploadId!)
}

/** Whether the walk has anything left to do — drives the finished screen. */
export function walkIsFinished(counts: WalkCounts): boolean {
  return counts.remaining === 0 && counts.inFlight === 0
}
